import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sprint 12 — Admin: Certificates management page.
// Route: /admin/certificates (JWT-protected). Follows the auth pattern used
// by the rest of /admin — reads the JWT from localStorage `gop_admin_jwt`.
export default function AdminCertificates() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ month: '', subject: '', mail_status: '' });
  const [sortMode, setSortMode] = useState('recent');   // 'recent' | 'viral'
  const [topSharers, setTopSharers] = useState([]);
  const [computing, setComputing] = useState(false);
  const [flash, setFlash] = useState(null);
  const [mailEditFor, setMailEditFor] = useState(null);
  const [addrDraft, setAddrDraft] = useState({ address: '', postal_code: '' });

  const jwt = typeof window !== 'undefined' ? localStorage.getItem('gop_admin_jwt') : null;
  const headers = jwt ? { Authorization: `Bearer ${jwt}` } : {};

  const load = () => {
    setLoading(true);
    const params = {};
    if (filters.month) params.month = filters.month;
    if (filters.subject) params.subject = filters.subject;
    if (filters.mail_status) params.mail_status = filters.mail_status;
    if (sortMode === 'viral') params.viral = 1;
    axios.get(`${API}/admin/certificates`, { headers, params })
      .then(r => setRows(r.data.certificates || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));

    // Task Q — top sharers leaderboard tile
    const topParams = {};
    if (filters.month) topParams.month = filters.month;
    axios.get(`${API}/admin/certificates/top-sharers`, { headers, params: topParams })
      .then(r => setTopSharers(r.data.top_sharers || []))
      .catch(() => setTopSharers([]));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ },
    [filters.month, filters.subject, filters.mail_status, sortMode]);

  const compute = async () => {
    setComputing(true);
    try {
      const r = await axios.post(`${API}/admin/certificates/compute-awards`, {}, { headers });
      setFlash(`Awarded ${r.data.new_certificates_awarded} new certificate(s) for ${r.data.quarter_key}.`);
      load();
    } catch (err) {
      setFlash(`Failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setComputing(false);
    }
  };

  const markPrinted = async (id) => {
    await axios.post(`${API}/admin/certificates/${id}/mark-printed`, {}, { headers });
    load();
  };
  const markMailed = async (id) => {
    const tracking = prompt('Tracking number (optional)?') || null;
    await axios.post(`${API}/admin/certificates/${id}/mark-mailed`,
                     { tracking_number: tracking }, { headers });
    load();
  };
  const regenerate = async (id) => {
    await axios.post(`${API}/admin/certificates/${id}/regenerate`, {}, { headers });
    setFlash(`Regenerated PDF + PNG for ${id.slice(0, 8)}\u2026`);
    load();
  };
  const exportCsv = async () => {
    try {
      const qs = filters.month ? `&month=${filters.month}` : '';
      const r = await axios.get(
        `${API}/admin/certificates/export.csv?mail_status=pending${qs}`,
        { headers, responseType: 'blob' },
      );
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `gop-certificates-${filters.month || 'all'}-pending.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setFlash('CSV downloaded.');
    } catch (err) {
      setFlash(`CSV export failed: ${err.response?.status || err.message}`);
    }
  };
  const saveAddress = async () => {
    if (!mailEditFor) return;
    await axios.post(`${API}/admin/certificates/${mailEditFor}/mail-address`,
                     addrDraft, { headers });
    setMailEditFor(null);
    setAddrDraft({ address: '', postal_code: '' });
    load();
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="admin-certificates-page">
      <div className="max-w-6xl mx-auto space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold">Certificates</h1>
            <p className="text-slate-500 text-sm">
              Manage top-10% awards, capture postal addresses, and mark print/mail status.
            </p>
          </div>
          <Button onClick={compute} disabled={computing}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="admin-compute-awards-btn">
            {computing ? 'Computing\u2026' : 'Compute this month\u2019s awards'}
          </Button>
          <Button onClick={exportCsv} variant="outline" className="border-slate-300"
                  data-testid="admin-cert-export-csv-btn">
            Export CSV for print-house
          </Button>
        </div>

        {flash && (
          <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900"
               data-testid="admin-cert-flash">{flash}</div>
        )}

        {/* Task Q — Top sharers leaderboard tile */}
        {topSharers.length > 0 && (
          <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-white"
                data-testid="admin-cert-top-sharers-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                🔥 Top sharers {filters.month ? `for ${filters.month}` : 'this month'}
              </CardTitle>
              <CardDescription>
                Certificates ranked by how many times their WhatsApp share button was tapped.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ol className="space-y-1" data-testid="admin-cert-top-sharers-list">
                {topSharers.map((c, idx) => (
                  <li key={c.id || c.verify_serial}
                      data-testid={`top-sharer-${idx + 1}`}
                      className="flex justify-between items-center gap-3 text-sm">
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="text-lg" aria-hidden>
                        {['🥇', '🥈', '🥉'][idx] || `#${idx + 1}`}
                      </span>
                      <span className="truncate">
                        <strong>{c.student_name}</strong>
                        <span className="text-slate-500 ml-2">
                          {c.subject} · {c.tier}
                        </span>
                      </span>
                    </span>
                    <span className="font-semibold text-emerald-700 whitespace-nowrap">
                      {c.share_count}× shares
                    </span>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Filters</CardTitle>
            <CardDescription>Narrow the table by award month, subject, or mail status.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2 items-end">
            <div>
              <label className="block text-xs text-slate-500">Month (YYYY-MM)</label>
              <Input value={filters.month}
                     onChange={(e) => setFilters(f => ({ ...f, month: e.target.value }))}
                     placeholder="2026-08" className="w-32"
                     data-testid="admin-cert-filter-month" />
            </div>
            <div>
              <label className="block text-xs text-slate-500">Subject</label>
              <select value={filters.subject}
                      onChange={(e) => setFilters(f => ({ ...f, subject: e.target.value }))}
                      className="border rounded px-2 py-2"
                      data-testid="admin-cert-filter-subject">
                <option value="">All</option>
                <option value="maths">Mathematics</option>
                <option value="english">English</option>
                <option value="science">Science</option>
                <option value="overall">Overall</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-500">Mail status</label>
              <select value={filters.mail_status}
                      onChange={(e) => setFilters(f => ({ ...f, mail_status: e.target.value }))}
                      className="border rounded px-2 py-2"
                      data-testid="admin-cert-filter-mail-status">
                <option value="">Any</option>
                <option value="pending">Pending print</option>
                <option value="printed">Printed, awaiting mail</option>
                <option value="mailed">Mailed</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-500">Sort</label>
              <select value={sortMode}
                      onChange={(e) => setSortMode(e.target.value)}
                      className="border rounded px-2 py-2"
                      data-testid="admin-cert-sort-mode">
                <option value="recent">Most recent</option>
                <option value="viral">Most shared (viral)</option>
              </select>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {loading ? 'Loading\u2026' : `${rows.length} certificate(s) · sorted by ${sortMode === 'viral' ? 'shares' : 'most recent'}`}
            </CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {rows.length === 0 && !loading ? (
              <p className="text-sm text-slate-500" data-testid="admin-cert-empty">
                No certificates match the current filters.
              </p>
            ) : (
              <table className="w-full text-sm" data-testid="admin-cert-table">
                <thead>
                  <tr className="text-left text-xs text-slate-500 uppercase">
                    <th className="px-2 py-2">Student</th>
                    <th className="px-2 py-2">Grade</th>
                    <th className="px-2 py-2">Subject</th>
                    <th className="px-2 py-2">Tier</th>
                    <th className="px-2 py-2">Serial</th>
                    <th className="px-2 py-2">Awarded</th>
                    <th className="px-2 py-2">Mail status</th>
                    <th className="px-2 py-2">Shared</th>
                    <th className="px-2 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => (
                    <tr key={row.id} className="border-t border-slate-100 align-top"
                        data-testid={`admin-cert-row-${row.id}`}>
                      <td className="px-2 py-2 font-medium">{row.student_name}</td>
                      <td className="px-2 py-2">{row.grade}</td>
                      <td className="px-2 py-2 capitalize">{row.subject}</td>
                      <td className="px-2 py-2">
                        <Badge className="bg-amber-100 text-amber-900 border border-amber-300">
                          {row.tier}
                        </Badge>
                      </td>
                      <td className="px-2 py-2 font-mono text-xs">{row.verify_serial}</td>
                      <td className="px-2 py-2 text-xs">
                        {row.awarded_at?.slice(0, 10)}
                      </td>
                      <td className="px-2 py-2 text-xs">
                        {row.mailed_at ? (
                          <Badge className="bg-emerald-100 text-emerald-900">
                            Mailed {row.mailed_at.slice(0, 10)}
                          </Badge>
                        ) : row.printed_at ? (
                          <Badge className="bg-sky-100 text-sky-900">
                            Printed {row.printed_at.slice(0, 10)}
                          </Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-700">Pending</Badge>
                        )}
                        {row.mail_address?.address && (
                          <p className="text-slate-500 mt-1">{row.mail_address.address}</p>
                        )}
                      </td>
                      <td className="px-2 py-2 text-center text-xs" data-testid={`admin-cert-share-count-${row.id}`}>
                        {row.share_count > 0 ? (
                          <span className="font-semibold text-emerald-700">{row.share_count}\u00d7</span>
                        ) : (
                          <span className="text-slate-400">&mdash;</span>
                        )}
                      </td>
                      <td className="px-2 py-2 space-x-1 whitespace-nowrap">
                        <a href={`${API}/certificate/${row.id}.pdf`}
                           target="_blank" rel="noreferrer"
                           className="underline text-emerald-700 text-xs">
                          PDF
                        </a>
                        <button onClick={() => { setMailEditFor(row.id);
                                                 setAddrDraft(row.mail_address || { address: '', postal_code: '' }); }}
                                className="text-xs underline text-sky-700"
                                data-testid={`admin-cert-addr-${row.id}`}>
                          Address
                        </button>
                        {!row.printed_at && (
                          <button onClick={() => markPrinted(row.id)}
                                  className="text-xs underline text-slate-700"
                                  data-testid={`admin-cert-mark-printed-${row.id}`}>
                            Mark printed
                          </button>
                        )}
                        {row.printed_at && !row.mailed_at && (
                          <button onClick={() => markMailed(row.id)}
                                  className="text-xs underline text-emerald-700"
                                  data-testid={`admin-cert-mark-mailed-${row.id}`}>
                            Mark mailed
                          </button>
                        )}
                        <button onClick={() => regenerate(row.id)}
                                className="text-xs underline text-amber-700">
                          Regen
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {mailEditFor && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-40"
               data-testid="admin-cert-addr-modal">
            <div className="bg-white p-6 rounded-lg max-w-md w-full space-y-3">
              <h3 className="text-lg font-semibold">Mail address</h3>
              <Input value={addrDraft.address}
                     onChange={(e) => setAddrDraft(a => ({ ...a, address: e.target.value }))}
                     placeholder="Full postal address" />
              <Input value={addrDraft.postal_code || ''}
                     onChange={(e) => setAddrDraft(a => ({ ...a, postal_code: e.target.value }))}
                     placeholder="Postal code (optional)" />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setMailEditFor(null)}>Cancel</Button>
                <Button onClick={saveAddress}>Save</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
